import { RichTextEditor, Link } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import Highlight from "@tiptap/extension-highlight";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Image from "@tiptap/extension-image";
import { Button, FileButton, Text } from "@mantine/core";
import { IconPhoto } from "@tabler/icons-react";
import { useRef } from "react";
import { useFileUpload } from "../../services/react-query";
import LinkControl from "./LinkControl";

// Every toolbar control caches the editor it first rendered with and keeps
// reading from that cached instance, so a control that outlives its editor
// calls `can()` on a destroyed one and throws. `useEditor` builds a fresh
// instance whenever it has to rebuild the editor — which it does whenever the
// mount effect that claims the first one is deferred, as React defers it for
// anything rendered inside a hidden Mantine tab panel. Keying the editor tree
// on the instance rebuilds the toolbar alongside the editor it reads from.
const editorKeys = new WeakMap();
let editorCount = 0;

const keyForEditor = (editor) => {
  if (!editor) return "no-editor";
  if (!editorKeys.has(editor)) {
    editorCount += 1;
    editorKeys.set(editor, `editor-${editorCount}`);
  }
  return editorKeys.get(editor);
};

const PagesEditor = ({ content = null, onChange, error, withImage = true }) => {
  const { mutateAsync } = useFileUpload();

  const resetRef = useRef();

  const uploadImage = async (file) => {
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);

    await mutateAsync(file, {
      onSuccess: (data) => {
        editor
          .chain()
          .focus()
          .setImage({
            src: previewUrl,
            alt: data.fileKey,
          })
          .run();
      },
    });
  };

  const editor = useEditor({
    shouldRerenderOnTransaction: true,
    extensions: [
      StarterKit.configure({ link: false }),
      Link,
      Highlight,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Image,
    ],
    content,
    onUpdate({ editor }) {
      onChange(JSON.stringify(editor.getJSON()));
    },
  });

  return (
    <RichTextEditor
      key={keyForEditor(editor)}
      editor={editor}
      mt="md"
      style={{ border: error && "1px solid red" }}
    >
      <RichTextEditor.Toolbar sticky stickyOffset={60}>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Bold />
          <RichTextEditor.Italic />
          <RichTextEditor.Underline />
          <RichTextEditor.Strikethrough />
          <RichTextEditor.ClearFormatting />
          <RichTextEditor.Highlight />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.H1 />
          <RichTextEditor.H2 />
          <RichTextEditor.H3 />
          <RichTextEditor.H4 />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Blockquote />
          <RichTextEditor.Hr />
          <RichTextEditor.BulletList />
          <RichTextEditor.OrderedList />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <LinkControl editor={editor} />
          <RichTextEditor.Unlink />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.AlignLeft />
          <RichTextEditor.AlignCenter />
          <RichTextEditor.AlignJustify />
          <RichTextEditor.AlignRight />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Undo />
          <RichTextEditor.Redo />
        </RichTextEditor.ControlsGroup>
        {withImage && (
          <FileButton
            onChange={uploadImage}
            resetRef={resetRef}
            accept="image/png,image/jpeg"
          >
            {(props) => (
              <Button
                size="compact-sm"
                radius="md"
                variant="default"
                {...props}
              >
                <IconPhoto size={16} />
              </Button>
            )}
          </FileButton>
        )}
      </RichTextEditor.Toolbar>

      <RichTextEditor.Content />
      {error && (
        <Text size="sm" c="red">
          {error.message}
        </Text>
      )}
    </RichTextEditor>
  );
};

export default PagesEditor;
